<template>
  <div :class="[$style.widgetContainer, positionClass]" v-if="loaded">
    <!-- Expanded View Area -->
    <div v-if="isExpanded" :class="$style.expandedViewContainer">
      <!-- 로딩 스피너 (인증 초기화 중) -->
      <div v-if="isInitializingAuth" :class="$style.loadingOverlay">
        <span>Loading...</span>
      </div>
      <!-- 인증/티켓 로딩 완료 후 내용 표시 -->
      <template v-else>
        <!-- 대화 시작 전: ChatExpanded 표시 -->
        <ChatExpanded
          v-if="currentView === 'expanded'"
          :recent-ticket="recentTicket"
          @start-chat="startChat"
          @load-recent-chat="loadRecentChat"
          @show-order-list="showOrderList"
          @close-chat="closeChatExpanded"
          :class="$style.expandedView"
        />
        <!-- 대화 시작 후: ChatWindow 표시 -->
        <ChatWindow
          v-else-if="currentView === 'chat' && recentTicket"
          :ticket="recentTicket"
          :initial-state="initialState"
          :author-id="user?.uid ?? 'unknown-author'"
          @close="closeChatWindow"
          @start-new-chat="handleStartNewChat"
          :class="$style.chatWindowView"
        />
        <!-- 주문 목록 표시 -->
        <OrderList
          v-else-if="currentView === 'orderList'"
          :orders="ordersForList"
          :limit="10"
          @inquire="handlePostOrder"
          @close="closeOrderList"
          :class="$style.orderList"
        />
      </template>
    </div>

    <!-- Minimized View Promo -->
    <div v-if="!isExpanded && showPromo && !isInitializingAuth" :class="$style.minimizedPromoContainer">
      <div :class="$style.promoGroup">
        <button :class="$style.promoCloseButton" @click="hidePromoBubble" aria-label="프로모션 숨기기">
          <XMarkIcon :class="$style.promoCloseIcon" />
        </button>
        <div :class="$style.promoBubble">{{ settings.promoText }}</div>
      </div>
    </div>

    <!-- Chat Toggle Button -->
    <ChatButton
      v-if="!isInitializingAuth"
      v-show="!isMobileExpanded"
      :is-loading="isWidgetLoading && !isInitializingAuth"
      @toggle-expand="toggleExpand"
      :expanded="isExpanded"
      :class="$style.chatButton"
    />

    <!-- Notification Modal -->
    <Notification
      v-if="notification"
      :type="notification.type"
      :message="notification.message"
      @close="closeNotification"
      :class="$style.notification"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, useCssModule, watch } from "vue"
import { XMarkIcon } from "@heroicons/vue/20/solid"
import ChatWindow from "./components/ChatWindow.vue"
import { useWidgetSettings } from "@/composables/useWidgetSettings"
import { useAuth } from "@/composables/useAuth"
import { useTicket } from "@/composables/useTicket"
import { initializeSubscriptions, cleanupSubscriptions } from "@/services/subscriptionManager"
import ChatButton from "@/components/ChatButton.vue"
import ChatExpanded from "@/components/ChatExpanded.vue"
import OrderList from "@/components/OrderList.vue"
import Notification from "@/components/Notification.vue"
import type { Order } from "@wowpress/types"

const { settings, loaded } = useWidgetSettings()
const $style = useCssModule()

const isInitializingAuth = ref(true)
const auth = useAuth()
const { user, loading: authLoading, error: authError, updateUserAndAuthenticate, signOutUser, signInAnonymously } = auth

const { createTicket, ticketLoading, ticketError, recentTicket } = useTicket()

const isWidgetLoading = computed(() => isInitializingAuth.value || authLoading.value || ticketLoading.value)

const isExpanded = ref(false)
const showPromo = ref(true)
const currentView = ref<"expanded" | "chat" | "orderList">("expanded")
const initialState = ref<"preOrder" | "postOrder" | "serviceInquiry">("serviceInquiry")
const ordersForList = ref<Order[]>([])
const notification = ref<{ type: "info" | "error"; message: string } | null>(null)
const isMobile = ref(window.innerWidth <= 480)

const positionClass = computed(() => {
  return `${$style[`position-${settings.value.position || "bottom-right"}`]}`
})

const isMobileExpanded = computed(() => {
  return isMobile.value && isExpanded.value
})

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value
  console.log("Toggle expand requested, isExpanded:", isExpanded.value)
  if (isExpanded.value) {
    hidePromoBubble()
    console.log("recentTicket.value:", recentTicket.value)
    if (recentTicket.value && recentTicket.value.status !== "closed") {
      console.log("[App.vue] Expanding widget: Active ticket exists, showing ChatWindow.")
      currentView.value = "chat"
    } else {
      console.log("[App.vue] Expanding widget: No active ticket, showing ChatExpanded.")
      currentView.value = "expanded"
    }
  } else {
    currentView.value = "expanded"
    notification.value = null
  }
}

const showOrderList = (orders: Order[]) => {
  ordersForList.value = orders
  currentView.value = "orderList"
}

const hidePromoBubble = () => {
  showPromo.value = false
}

const handlePostOrder = (order: Order) => {
  console.log("Inquire requested, order:", order)
  startChat("postOrder", order)
}

const closeOrderList = () => {
  currentView.value = "expanded"
}

const startChat = async (type: "preOrder" | "postOrder" | "serviceInquiry", order?: Order) => {
  console.log("Start chat requested, type:", type, "User:", user.value?.uid)

  // 사용자가 로그인되어 있지 않으면 익명 로그인 시도
  if (!user.value) {
    console.log("User not authenticated, attempting anonymous sign-in...")
    try {
      await signInAnonymously()

      // 익명 로그인 후 잠시 기다려서 user 상태가 업데이트되도록 함
      // onAuthStateChanged가 user.value를 업데이트할 때까지 기다림
      let retries = 10
      while (!user.value && retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, 100))
        retries--
      }

      if (!user.value) {
        console.error("Anonymous sign-in completed but user is still null")
        showNotification({ type: "error", message: "로그인에 실패했습니다. 다시 시도해주세요." })
        return
      }
      console.log("Anonymous sign-in successful, user:", (user.value as any)?.uid)
    } catch (error) {
      console.error("Anonymous sign-in failed:", error)
      showNotification({ type: "error", message: "로그인에 실패했습니다. 다시 시도해주세요." })
      return
    }
  }

  if (ticketLoading.value) {
    console.error("Cannot start chat: Ticket operation in progress.")
    return
  }

  initialState.value = type
  const initialMessage =
    type === "preOrder"
      ? "주문 전, 문의사항이 있어요."
      : type === "postOrder"
        ? "주문 후, 문의사항이 있어요."
        : "서비스 이용에 대해 궁금한게 있어요"
  const newTicketId = await createTicket({ type, initialMessage, order })
  if (newTicketId) {
    console.log("Ticket created successfully:", newTicketId)
    currentView.value = "chat"
    isExpanded.value = true
  } else {
    console.error("Failed to create ticket.", ticketError.value)
    showNotification({ type: "error", message: "상담 시작에 실패했습니다." })
  }
}

const loadRecentChat = () => {
  console.log("Load recent chat requested")
  if (!user.value) {
    console.error("Cannot load recent chat: User is not authenticated.")
    return
  }
  if (recentTicket.value) {
    console.log("Active recent ticket exists, activating chat window:", recentTicket.value.id)
    currentView.value = "chat"
    isExpanded.value = true
  } else {
    console.log("No active recent ticket found to load. Showing expanded view.")
    currentView.value = "expanded"
  }
}

const closeChatWindow = () => {
  currentView.value = "expanded"
  console.log("[App.vue] ChatWindow closed, returning to ChatExpanded view.")
}

const handleStartNewChat = () => {
  console.log("[App.vue] Start new chat requested, switching back to expanded view.")
  currentView.value = "expanded"
  isExpanded.value = true
}

const closeChatExpanded = () => {
  console.log("[App.vue] ChatExpanded close requested.")
  isExpanded.value = false
  currentView.value = "expanded"
}

const showNotification = (payload: { type: "info" | "error"; message: string }) => {
  console.log(`[App.vue] Notification: ${payload.type} - ${payload.message}`)
  notification.value = payload
  setTimeout(() => {
    if (notification.value?.message === payload.message) {
      notification.value = null
    }
  }, 5000)
}

const closeNotification = () => {
  notification.value = null
}

const widgetState = ref("closed")

const handlePostMessage = async (event: MessageEvent) => {
  console.log("[App.vue] postMessage 수신:", event.origin, event.data)

  //if (event.origin !== 'https://your-main-site.com') return;

  if (!event.data || typeof event.data !== "object") {
    console.log("[App.vue] 올바르지 않은 메시지 형식:", event.data)
    return
  }

  if (event.data.type === "UPDATE_CHAT_INFO") {
    let success = false
    let postMessageError: string | null = null
    const payload = event.data.payload

    try {
      if (payload && typeof payload === "string") {
        console.log("[App.vue] 사용자 데이터 업데이트 요청 수신 (페이로드 있음)")
        success = await updateUserAndAuthenticate(payload)
        if (!success) {
          postMessageError = authError.value?.message || "사용자 인증 실패"
        }
      } else if (payload === "") {
        console.log("[App.vue] 사용자 데이터 업데이트 요청 수신 (페이로드 없음 - 로그아웃 처리)")

        // 이미 익명 사용자가 로그인되어 있으면 로그아웃하지 않음
        if (user.value?.isAnonymous) {
          console.log("[App.vue] 이미 익명 사용자로 로그인됨. 로그아웃 건너뛰기")
          success = true
        } else {
          await signOutUser()
          await signInAnonymously()
          success = true
          console.log("[App.vue] 로그아웃 및 익명 로그인 처리 완료")
        }
      } else {
        console.warn("[App.vue] 유효하지 않은 chatInfo 페이로드 수신:", payload)
        postMessageError = "유효하지 않은 페이로드 형식"
      }
    } catch (error: any) {
      console.error("[App.vue] chatInfo 처리 중 오류:", error)
      success = false
      postMessageError = error.message || "chatInfo 처리 중 오류 발생"
    }

    if (event.source) {
      event.source.postMessage(
        {
          type: "CHAT_INFO_PROCESSED",
          success,
          error: postMessageError,
        },
        { targetOrigin: event.origin },
      )
    } else {
      console.warn("[App.vue] 메시지를 보낸 소스 창을 찾을 수 없습니다.")
    }

    if (success) {
      console.log("[App.vue] chatInfo 처리 성공")
    } else {
      console.warn("[App.vue] chatInfo 처리 실패", postMessageError)
    }
  } else if (event.data.type === "TOGGLE_WIDGET") {
    widgetState.value = widgetState.value === "open" ? "closed" : "open"
  }
}

onMounted(async () => {
  isInitializingAuth.value = true
  try {
    console.log("Widget mounted, checking external auth status...")

    window.addEventListener("message", handlePostMessage)
    window.addEventListener("resize", handleResize)
    console.log("[App.vue] postMessage 및 resize 이벤트 리스너 등록 완료")

    await signInAnonymously()

    console.log(
      "Initial authentication check finished.",
      authError.value ? `Error: ${authError.value.message}` : "Sign-in attempt completed.",
    )
  } catch (e) {
    console.error("Error during initial auth check process:", e)
  } finally {
    isInitializingAuth.value = false
  }

  window.parent.postMessage({ type: "WIDGET_READY" }, "*")
})

onUnmounted(() => {
  window.removeEventListener("message", handlePostMessage)
  window.removeEventListener("resize", handleResize)
  console.log("[App.vue] 이벤트 리스너 제거 완료")
  // body 스크롤 복원
  enableBodyScroll()
  auth.dispose()
  console.log("[App.vue] Auth instance disposed.")
})

watch(
  user,
  (currentUser, previousUser) => {
    const currentUserId = currentUser?.uid
    const previousUserId = previousUser?.uid
    console.log(
      `[App.vue] Watch user triggered. Current: ${currentUserId ?? "null"}, Previous: ${previousUserId ?? "null"}`,
    )

    if (currentUserId !== previousUserId) {
      if (currentUserId) {
        console.log("[App.vue] User changed or logged in, initializing subscriptions:", currentUserId)
        initializeSubscriptions(currentUserId)
        currentView.value = "expanded"
      } else {
        console.log("[App.vue] User logged out, cleaning up subscriptions.")
        cleanupSubscriptions()
        currentView.value = "expanded"
        isExpanded.value = false
        notification.value = null
      }
    } else {
      console.log("[App.vue] Watch user triggered, but user ID did not change.")
    }
  },
  { immediate: true },
)

// 모바일 body 스크롤 제어 함수
const disableBodyScroll = () => {
  if (isMobile.value) {
    document.body.style.overflow = "hidden"
    document.body.style.position = "fixed"
    document.body.style.width = "100%"
    document.body.style.top = `-${window.scrollY}px`
  }
}

const enableBodyScroll = () => {
  if (isMobile.value) {
    const scrollY = document.body.style.top
    document.body.style.overflow = ""
    document.body.style.position = ""
    document.body.style.width = ""
    document.body.style.top = ""
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY || "0") * -1)
    }
  } else {
    // 데스크톱에서는 항상 스크롤 복원
    document.body.style.overflow = ""
    document.body.style.position = ""
    document.body.style.width = ""
    document.body.style.top = ""
  }
}

// 창 크기 변경 처리
const handleResize = () => {
  const newIsMobile = window.innerWidth <= 480
  if (isMobile.value !== newIsMobile) {
    isMobile.value = newIsMobile
  }

  if (isExpanded.value) {
    if (isMobile.value) {
      disableBodyScroll()
    } else {
      enableBodyScroll()
    }
  }
}

// isExpanded 상태 감지하여 body 스크롤 제어
watch(
  isExpanded,
  (expanded) => {
    if (expanded) {
      disableBodyScroll()
    } else {
      enableBodyScroll()
    }
  },
  { immediate: false },
)

watch(
  recentTicket,
  (newTicket, oldTicket) => {
    console.log(
      `[App.vue] watch(recentTicket) triggered.`,
      `newTicket: ${newTicket?.id}(${newTicket?.status})`,
      `oldTicket: ${oldTicket?.id}(${oldTicket?.status})`,
      `isExpanded: ${isExpanded.value}`,
      `currentView: ${currentView.value}`,
    )

    if (!isExpanded.value) {
      console.log("[App.vue] Widget is not expanded, skipping view update based on ticket change.")
      return
    }

    if (newTicket && newTicket.status !== "closed" && newTicket.status !== "resolved") {
      if (currentView.value !== "chat") {
        console.log("[App.vue] Valid ticket detected, activating chat view.")
        currentView.value = "chat"
      } else {
        console.log("[App.vue] Valid ticket detected and current view is already chat. No view change needed.")
      }
    } else {
      if (currentView.value === "chat") {
        console.log("[App.vue] Ticket closed/resolved but staying in chat view as user is currently viewing it.")
      } else if (oldTicket && oldTicket.status !== "closed" && oldTicket.status !== "resolved") {
        console.log("[App.vue] Ticket removed or closed, switching back to expanded view (from non-chat view).")
        currentView.value = "expanded"
      } else {
        console.log("[App.vue] No valid/active ticket, ensuring expanded view is shown (if not already).")
        if (currentView.value !== "expanded") {
          currentView.value = "expanded"
          console.log("[App.vue] Switched back to expanded view from another view.")
        }
      }
    }
  },
  { immediate: true },
)
</script>

<style module>
.widgetContainer {
  --wowpress-primary-color: #cf1578;
  --wowpress-secondary-color: #ffecf6;
  --wowpress-bg-color: #ffffff;
  --wowpress-text-color: #1f2937;

  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  z-index: 99999;
  box-sizing: border-box;
}

.position-bottom-right {
  position: fixed;
  right: 20px;
  bottom: 20px;
  width: auto;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.position-bottom-left {
  position: fixed;
  left: 20px;
  bottom: 20px;
  width: auto;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.expandedViewContainer {
  position: relative;
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.minimizedPromoContainer {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.promoGroup {
  display: flex;
  align-items: center;
  gap: 12px;
}

.promoBubble {
  position: relative;
  background-color: #ffffff;
  color: #111111;
  padding: 14px 20px;
  border-radius: 12px;
  font-family: "Noto Sans KR", sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  box-shadow:
    0px 6px 12px -4px rgba(12, 12, 13, 0.08),
    0px 0px 20px -6px rgba(12, 12, 13, 0.12);
}

.promoBubble::after {
  content: "";
  position: absolute;
  bottom: -5px;
  right: 20px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #ffffff;
  filter: drop-shadow(0px 2px 1px rgba(12, 12, 13, 0.05));
}

.promoCloseButton {
  width: 28px;
  height: 28px;
  background-color: #ffffff;
  border-radius: 50%;
  box-shadow:
    0px 2px 4px -2px rgba(12, 12, 13, 0.06),
    0px 0px 10px -4px rgba(12, 12, 13, 0.1);
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    transform 0.2s ease-out,
    box-shadow 0.2s ease-out;
  z-index: 1;
}

.promoCloseButton:hover {
  transform: scale(1.05);
}

.promoCloseIcon {
  width: 16px;
  height: 16px;
  color: #6b7280;
}

.promoCloseButton:hover .promoCloseIcon {
  color: #1f2937;
}

.loadingOverlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 16px;
  font-size: 14px;
  color: #333;
}

.expandedView,
.chatWindowView {
  width: 343px;
  height: 583px;
  border-radius: 16px;
  box-shadow:
    5px 14px 80px 0px rgba(26, 26, 26, 0.06),
    10px 10px 40px 0px rgba(0, 0, 0, 0.04);
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chatButton {
  position: relative;
  z-index: 1;
}

@media (max-width: 480px) {
  .position-bottom-right,
  .position-bottom-left {
    position: fixed;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
    padding: 0;
    background-color: transparent;
  }

  .expandedViewContainer {
    width: 100%;
    height: 100%;
    margin-bottom: 0;
    position: absolute;
    inset: 0;
    z-index: 5;
  }

  .loadingOverlay {
    border-radius: 0;
  }
  .expandedView,
  .chatWindowView {
    width: 100%;
    height: 100%;
    border-radius: 0;
    box-shadow: none;
  }

  .position-bottom-right > button[class*="chatButton"],
  .position-bottom-left > button[class*="chatButton"] {
    position: fixed;
    z-index: 10;
    bottom: 10px;
  }
  .position-bottom-right > button[class*="chatButton"] {
    right: 10px;
  }
  .position-bottom-left > button[class*="chatButton"] {
    left: 10px;
  }

  .minimizedPromoContainer {
    display: none;
  }
}
</style>
